import { useMemo, useState } from "react";
import { ChevronDown, Minus, Plus } from "lucide-react";
import {
  AVATAR_FALLBACK,
  formatEarningsCurrency,
  ReferralTreeNode,
} from "../earnings/earningsAdapters";

const findNode = (
  node: ReferralTreeNode,
  id: string | null,
): ReferralTreeNode | null => {
  if (!id) return null;
  if (node.id === id) return node;
  for (const child of node.children ?? []) {
    const result = findNode(child, id);
    if (result) return result;
  }
  return null;
};

// Rail spans from the first child's center to the last child's center.
const railWidth = (count: number) =>
  count > 1 ? `${((count - 1) / count) * 100}%` : "0%";

interface Props {
  data: ReferralTreeNode;
  title: string;
  subtitle: string;
}

const ReferralTree = ({ data, title, subtitle }: Props) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  const directChildIds = useMemo(() => {
    const activeNode = findNode(data, activeId);
    return new Set(
      (activeNode?.children ?? []).map((child) => child.id),
    );
  }, [data, activeId]);

  const nodeClass = (node: ReferralTreeNode) =>
    [
      "ri-tree-person",
      activeId === node.id ? "is-parent" : "",
      directChildIds.has(node.id) ? "is-direct-child" : "",
    ]
      .filter(Boolean)
      .join(" ");

  const CommissionBadge = ({ node }: { node: ReferralTreeNode }) => {
    const flow = node.commissionAmount ?? 0;
    const pearls = node.salarCoins ?? 0;
    if (flow <= 0 && pearls <= 0) return null;
    return (
      <div className="ri-tree-commission">
        <small>RECEIVED</small>
        <strong>{formatEarningsCurrency(flow)}</strong>
        {pearls > 0 && <small>+{pearls} pearls</small>}
      </div>
    );
  };

  const PersonCard = ({ node }: { node: ReferralTreeNode }) => (
    <article
      className={nodeClass(node)}
      onMouseEnter={() => setActiveId(node.id)}
      onMouseLeave={() => setActiveId(null)}
    >
      <img src={node.photo || AVATAR_FALLBACK} alt="" />
      <strong>{node.name}</strong>
      {node.userId ? <span>{node.userId}</span> : null}
      <CommissionBadge node={node} />
    </article>
  );

  const DesktopChildren = ({ nodes }: { nodes: ReferralTreeNode[] }) => (
    <div
      className="ri-tree-children"
      style={{
        gridTemplateColumns: `repeat(${nodes.length}, minmax(150px, 1fr))`,
      }}
    >
      {nodes.map((child) => {
        const grandChildren = child.children ?? [];
        return (
          <div className="ri-tree-child-slot" key={child.id}>
            <div className="ri-tree-child-stem" />
            <PersonCard node={child} />
            {grandChildren.length > 0 && (
              <>
                <div className="ri-tree-branch-down" />
                {grandChildren.length > 1 && (
                  <div
                    className="ri-tree-child-rail"
                    style={{ width: railWidth(grandChildren.length) }}
                  />
                )}
                <DesktopChildren nodes={grandChildren} />
              </>
            )}
          </div>
        );
      })}
    </div>
  );

  const MobileNode = ({
    node,
    level,
  }: {
    node: ReferralTreeNode;
    level: number;
  }) => (
    <article
      className={nodeClass(node)}
      onClick={() => setActiveId(activeId === node.id ? null : node.id)}
    >
      <img src={node.photo || AVATAR_FALLBACK} alt="" />
      <div>
        <small>{level === 0 ? "ROOT MEMBER" : `LEVEL ${level}`}</small>
        <strong>{node.name}</strong>
        {node.userId ? <span>{node.userId}</span> : null}
        <CommissionBadge node={node} />
      </div>
      <ChevronDown size={18} />
    </article>
  );

  const MobileChildren = ({
    nodes,
    level,
  }: {
    nodes: ReferralTreeNode[];
    level: number;
  }) => (
    <div className="ri-tree-mobile-children">
      {nodes.map((child) => (
        <div key={child.id}>
          <MobileNode node={child} level={level} />
          {child.children?.length > 0 && (
            <MobileChildren nodes={child.children} level={level + 1} />
          )}
        </div>
      ))}
    </div>
  );

  const children = data.children ?? [];
  const branchCount = children.length;

  return (
    <section className="ri-tree-section">
      <header className="ri-tree-topbar">
        <div>
          <span className="ri-eyebrow">NETWORK TREE</span>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        <div className="ri-tree-zoom">
          <button
            type="button"
            onClick={() =>
              setZoom((current) =>
                Math.max(0.86, Number((current - 0.08).toFixed(2))),
              )
            }
            aria-label="Zoom out"
          >
            <Minus size={18} />
          </button>
          <strong>{Math.round(zoom * 100)}%</strong>
          <button
            type="button"
            onClick={() =>
              setZoom((current) =>
                Math.min(1.18, Number((current + 0.08).toFixed(2))),
              )
            }
            aria-label="Zoom in"
          >
            <Plus size={18} />
          </button>
        </div>
      </header>

      <div className="ri-tree-desktop-scroll">
        <div
          className="ri-tree-desktop-canvas"
          style={{ transform: `scale(${zoom})` }}
        >
          <div className="ri-tree-root-row">
            <PersonCard node={data} />
          </div>
          {branchCount > 0 && (
            <>
              <div className="ri-tree-root-stem" />
              {branchCount > 1 && (
                <div
                  className="ri-tree-main-rail"
                  style={{ width: railWidth(branchCount) }}
                />
              )}
              <div
                className="ri-tree-branches"
                style={{
                  gridTemplateColumns: `repeat(${branchCount}, minmax(180px, 1fr))`,
                }}
              >
                {children.map((branch) => {
                  const branchChildren = branch.children ?? [];
                  return (
                    <div className="ri-tree-branch" key={branch.id}>
                      <div className="ri-tree-branch-stem" />
                      <PersonCard node={branch} />
                      {branchChildren.length > 0 && (
                        <>
                          <div className="ri-tree-branch-down" />
                          {branchChildren.length > 1 && (
                            <div
                              className="ri-tree-child-rail"
                              style={{
                                width: railWidth(branchChildren.length),
                              }}
                            />
                          )}
                          <DesktopChildren nodes={branchChildren} />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="ri-tree-mobile">
        <MobileNode node={data} level={0} />
        <div className="ri-tree-mobile-spine" />
        <div className="ri-tree-mobile-branches">
          {children.map((branch) => (
            <section className="ri-tree-mobile-branch" key={branch.id}>
              <MobileNode node={branch} level={1} />
              {branch.children?.length > 0 && (
                <MobileChildren nodes={branch.children} level={2} />
              )}
            </section>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReferralTree;
